import React from 'react';
import { findIndex } from 'lodash';
import classnames from 'classnames';

import style from './index.scss';
import {
  findMeanIndex,
  formatDecimal,
  formatNumeric,
  interpolateColors
} from '../../../util';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import PopoutWindow from '../../PopoutWindow';
import { HoverableDIV } from '../../HoverableTD';
import { Select } from 'antd';
import {
  deviation_down,
  deviation_up,
  regex,
  riskIndicatorNameMap
} from './riskIndicatorUtil';
import {
  convertToFormattedMoney,
  getAlertIDFromURL,
  getInDecimal
} from '../../../services/util';
import { binPeerComparisonArray } from '../../../services/formattterUtil';
import NoDataToDisplay from '../../NoDataToDisplay';

const { Option } = Select;

const GraphToolTip = ({ txt }) => {
  return (
    <div>
      <div style={{ color: 'gray' }}>Description</div>
      <br />
      {txt}
    </div>
  );
};

const PeerComparisonToolTip = (props) => {
  const { active, payload } = props;
  let { label } = props || {};
  const barPayload = payload[0]?.payload ? payload[0].payload : null;
  try {
    const tags = label.match(regex);
    if (tags.length > 2) {
      const val1 = Number(tags[1]).toFixed(2);
      const val2 = Number(tags[2]).toFixed(2);
      label = `(${val1} , ${val2})`;
    }
  } catch (e) {
    // do nothing
  }

  return active ? (
    <div className={style.customTooltip}>
      <div className="row">
        <div className="col-sm-12">
          <p>
            <b>Range:</b> {label}
          </p>
          <p>
            <b>Distribution:</b> {Number(barPayload.value).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  ) : null;
};

class ClusterAttributes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clusterAttributes: [],
      filterType: 'All'
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps?.clusterAttributes &&
      nextProps.clusterAttributes.length !== 0
    ) {
      this.setState({ clusterAttributes: nextProps.clusterAttributes });
    }
    if (nextProps?.filterType) {
      this.setState({ filterType: nextProps.filterType });
    }
  }

  getRiskClass(riskName) {
    if (riskName === 'H') {
      return style.risk_high;
    }
    if (riskName === 'M') {
      return style.risk_medium;
    }
    if (riskName === 'L') {
      return style.risk_low;
    }
    return style.risk_low;
  }

  renderedAttributes = () => {
    const { clusterAttributes } = this.state || {};
    const { attributes } = clusterAttributes || {};
    if (Array.isArray(attributes) && attributes.length > 0) {
      const newAttributes = [...attributes, ...attributes, ...attributes];
      let filteredAttributes = [];
      if (this.state.filterType !== 'All') {
        filteredAttributes = newAttributes.filter((item) => {
          return item.comparison === this.state.filterType;
        });
      }
      if (filteredAttributes.length === 0) {
        if (this.state.filterType !== 'All') {
          filteredAttributes = [];
        } else {
          filteredAttributes = newAttributes;
        }
      }
      if (filteredAttributes.length === 0) {
        return (
          <li style={{ listStyle: 'none' }}>
            <div style={{ marginBottom: 15, color: 'white' }}>
              Filter has no data
            </div>
          </li>
        );
      }
      return filteredAttributes.map((o) => {
        return (
          <li style={{ listStyle: 'none' }}>
            <div style={{ display: 'flex', marginBottom: 15 }}>
              <div className={classnames(this.getRiskClass(o.comparison))}>
                {o.comparison}
              </div>
              <div className={style.riskLabelStyle}>{o.name}</div>
            </div>
          </li>
        );
      });
    } else {
      return null;
    }
  };

  render() {
    return (
      <div className={classnames(style.bg_dark)}>
        <div className={style.featureViewLabel}>Features</div>
        <ul style={{ columnCount: 4 }}>{this.renderedAttributes()}</ul>
      </div>
    );
  }
}

class RiskIndicatorBody extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterType: ''
    };
  }

  onFilterClicked = (flt) => {
    this.setState({ filterType: flt });
  };

  getClusterCount = () => {
    let low = 0;
    let mid = 0;
    let hi = 0;
    const { clusterData } = this.props;
    if (clusterData) {
      const { attributes } = clusterData;
      if (Array.isArray(attributes) && attributes.length > 0) {
        attributes.forEach((o) => {
          if (o.comparison === 'H') {
            hi += 1;
          } else if (o.comparison === 'M') {
            mid += 1;
          } else if (o.comparison === 'L') {
            low += 1;
          }
        });
      }
    }
    return { low, mid, hi };
  };

  render() {
    const { riskIndicator, isPluralView } = this.props;

    // check if there is risk indicator graph data
    if (riskIndicator) {
      const risk_indicator_name = riskIndicator
        ? riskIndicator.riskIndicatorName
        : '';
      const importance = Number(riskIndicator.importance).toFixed(2) || 0.0;

      const selfCompare = riskIndicator ? riskIndicator.self_comparision : null;
      const self_mean = selfCompare ? selfCompare.mean : '';
      const self_deviation = selfCompare ? selfCompare.deviation : '';
      const self_title = selfCompare ? selfCompare.title : '';
      let self_data_points = selfCompare ? selfCompare.data_points : [];
      const peerCompare = riskIndicator ? riskIndicator.peer_comparison : null;
      const peer_mean = peerCompare ? peerCompare.mean : '';
      let formatted_peer_mean = '';
      const peer_deviation = peerCompare ? peerCompare.deviation : '';
      const peer_title = peerCompare ? peerCompare.title : '';
      let peer_data_points = peerCompare ? peerCompare.data_points : [];
      peer_data_points = binPeerComparisonArray(peer_data_points);
      //for linear gradient from left to right
      const peerDataSize = peer_data_points ? peer_data_points.length : 0;
      const startColor = 'rgb(150, 171, 217)';
      const endColor = 'rgb(241,42,42)';
      let colorArray = [];
      let meanInd = peerCompare ? findMeanIndex(peerCompare) : 0;
      colorArray = interpolateColors(
        startColor,
        endColor,
        peerDataSize - meanInd
      );

      const peer_percentile = peerCompare ? peerCompare.percentile : '';
      const peer_current_value = peerCompare ? peerCompare.current_value : {};
      const peer_ri_value = peer_current_value?.riValue
        ? peer_current_value.riValue
        : 0;
      let pcXAxisValue;
      let pcYAxisValue;
      let pdXAxisValue;
      let pdYAxisValue;
      try {
        pcXAxisValue =
          riskIndicatorNameMap[risk_indicator_name]['peer_comparison']
            .xAxisValue;
        pcYAxisValue =
          riskIndicatorNameMap[risk_indicator_name]['peer_comparison']
            .yAxisValue;
        pdXAxisValue =
          riskIndicatorNameMap[risk_indicator_name]['profile_deviation']
            .xAxisValue;
        pdYAxisValue =
          riskIndicatorNameMap[risk_indicator_name]['profile_deviation']
            .yAxisValue;
      } catch (e) {}

      let customerDotIndex = null;
      let customerDotIndexYAxis = 0;
      let meanLineIndex = null;
      peer_data_points.map((o, idx) => {
        if (o.count !== 'nan') {
          const arrayOfY = o.count.replace('(', '').replace(')', '').split(',');
          let sumOfY = 0;
          arrayOfY.map((y) => {
            sumOfY += Number(y);
          });
          // range = sumOfY / arrayOfY.length

          if (arrayOfY.length === 1) {
            if (arrayOfY[0] === peer_current_value['riValue']) {
              customerDotIndex = idx;
              customerDotIndexYAxis = o.value;
            }
          }
          if (arrayOfY.length > 1) {
            if (
              arrayOfY[1] >= peer_current_value['riValue'] &&
              peer_current_value['riValue'] >= arrayOfY[0]
            ) {
              customerDotIndex = idx;
              customerDotIndexYAxis = o.value;
            } else if (peer_current_value['riValue'] < arrayOfY[0]) {
              customerDotIndex = 0;
              customerDotIndexYAxis = peer_data_points[0].value;
            } else if (peer_current_value['riValue'] > arrayOfY[1]) {
              customerDotIndex = peer_data_points.length - 1;
              customerDotIndexYAxis =
                peer_data_points[peer_data_points.length - 1].value;
            }
          }
          if (arrayOfY.length === 1) {
            if (arrayOfY[0] === peer_mean) {
              meanLineIndex = idx;
            }
          }
          if (arrayOfY.length > 1) {
            if (arrayOfY[1] >= peer_mean && peer_mean >= arrayOfY[0]) {
              meanLineIndex = idx;
            }
          }
        }
      });

      const new_peer_data_points = peer_data_points.map((o, idx) => {
        try {
          let oldLabel = o.count;
          const tags = oldLabel.match(regex);
          const { deviationYAxisName = '' } = riskIndicator || {};
          if (Array.isArray(tags) && tags.length > 2) {
            let val1 = '';
            let val2 = '';
            if (deviationYAxisName === 'amount') {
              val1 = convertToFormattedMoney(tags[1]);
              val2 = convertToFormattedMoney(tags[2]);
              formatted_peer_mean = convertToFormattedMoney(peer_mean);
            } else {
              val1 = formatNumeric(Number(tags[1]).toFixed(2));
              val2 = formatNumeric(Number(tags[2]).toFixed(2));
              formatted_peer_mean = formatNumeric(Number(peer_mean).toFixed(2));
            }

            const newLabel = `(${val1} , ${val2})`;
            return {
              ...o,
              value: Math.round((o.value + 0.00001) * 100) / 100,
              percentile: peer_percentile,
              riValue: peer_ri_value,
              count: newLabel
            };
          }
        } catch (e) {
          // do nothing
        }
        return { ...o };
      });

      let peerMeanLineIndex = 0;
      const peerMeanLineLength = new_peer_data_points.length;
      let requiredMeanLineIndex = meanLineIndex + 1;
      try {
        if (
          meanLineIndex !== null &&
          requiredMeanLineIndex < peerMeanLineLength
        ) {
          peerMeanLineIndex = new_peer_data_points[requiredMeanLineIndex].count;
        } else {
          peerMeanLineIndex =
            new_peer_data_points[peerMeanLineLength - 1].count;
        }
      } catch (e) {
        peerMeanLineIndex = 0;
      }
      return (
        <div style={{ overflow: 'hidden' }}>
          {!isPluralView && (
            <div className={style.indicatorNameBlock}>
              <div
                style={{ background: 'white' }}
                className="card-header pt-0 d-flex justify-content-start align-items-center border-bottom">
                <Select
                  dropdownStyle={{ minWidth: '40%' }}
                  style={{ minWidth: '25%', height: '32px' }}
                  placeholder="Risk Indicator"
                  onChange={this.props.loadSelected}
                  showSearch>
                  {Array.isArray(this.props.riskIndicators)
                    ? this.props.riskIndicators.map(
                        (riskIndicatorItem, riskIndicatorItemKey) => {
                          return (
                            <Option
                              key={riskIndicatorItemKey}
                              value={riskIndicatorItem.riskIndicatorName}>
                              {riskIndicatorItem.riskIndicatorName}
                            </Option>
                          );
                        }
                      )
                    : null}
                </Select>
                <a
                  onClick={this.props.togglePopout}
                  className={style.viewAllClass}>
                  View all Risk Indicators
                </a>
              </div>

              <div className={classnames('row', style.pad8)}>
                <div className="col-sm-12" style={{ display: 'flex' }}>
                  <div
                    className={classnames(style.sliderContainer, 'col-sm-5')}
                    style={{ textAlign: 'left' }}>
                    <a className={style.arrowStyle}>
                      <i
                        className={classnames(
                          'icon14',
                          'icon14_icon_arrow_left'
                        )}
                        onClick={this.props.loadPrevious}
                      />
                    </a>
                    <span>{risk_indicator_name}</span>
                    <a className={style.arrowStyle}>
                      <i
                        className={classnames(
                          'icon14',
                          'icon14_icon_arrow_right'
                        )}
                        onClick={this.props.loadNext}
                      />
                    </a>
                  </div>

                  <div
                    className={classnames('col-sm-4')}
                    style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      className={style.bordered}
                      style={{ width: '40%', textAlign: 'center' }}>
                      <HoverableDIV
                        firstDataPoint={
                          <GraphToolTip
                            txt={
                              'The overall importance indicates ' +
                              'the entropy of this cluster for a ' +
                              'particular risk indicator. ' +
                              'The higher the value the ' +
                              'higher the importance of the cluster'
                            }
                          />
                        }>
                        <span className={style.importanceLabel}>
                          Overall importance
                        </span>
                        <span className={style.importanceValue}>
                          {importance}
                        </span>
                      </HoverableDIV>
                    </div>
                  </div>

                  <div
                    className="col-sm-4"
                    style={{ textAlign: 'right' }}></div>
                </div>
              </div>
            </div>
          )}
          {isPluralView && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#335777',
                padding: 10
              }}>
              <span
                style={{
                  color: 'white',
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 100,
                  marginRight: 10
                }}>
                Risk Indicator:{' '}
              </span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'white'
                }}>
                {risk_indicator_name}
              </span>
              <div
                className={classnames('col-sm-4')}
                style={{ display: 'flex', justifyContent: 'center' }}>
                <div
                  className={style.bordered}
                  style={{ width: '40%', textAlign: 'center' }}>
                  <HoverableDIV
                    firstDataPoint={
                      <GraphToolTip
                        txt={
                          'The overall importance indicates the entropy of ' +
                          'this cluster for a particular risk indicator. ' +
                          'The higher the value the higher ' +
                          'the importance of the cluster'
                        }
                      />
                    }>
                    <span
                      className={classnames(
                        style.importanceLabel,
                        style.colorWhite
                      )}>
                      Overall importance
                    </span>
                    <span
                      className={classnames(
                        style.importanceValue,
                        style.colorLightGray
                      )}>
                      {importance}
                    </span>
                  </HoverableDIV>
                </div>
              </div>
            </div>
          )}
          <div className={classnames('row', 'clearfix', 'card-body')}>
            <div
              className={classnames(
                'col-sm-6',
                style.padSide,
                style.bdrRite,
                style.graphBody
              )}>
              <div className={style.customerRiskHead}>
                <span>Profile Deviation</span>&nbsp;&nbsp;
                <div className="deviation" id="pdValue">
                  <HoverableDIV
                    firstDataPoint={
                      <GraphToolTip
                        txt={
                          'The historical variation ' +
                          '(trend over the time) is reflected by ' +
                          'upward or downward trend of profile ' +
                          'deviation value. The higher ' +
                          'the value the riskier profile'
                        }
                      />
                    }>
                    <div className={style.graphIndicatorContainer}>
                      <img
                        style={{ marginRight: 5, height: 12 }}
                        src={
                          getInDecimal(self_deviation) >= 0
                            ? deviation_up
                            : deviation_down
                        }
                        alt={
                          getInDecimal(self_deviation) >= 0
                            ? deviation_up
                            : deviation_down
                        }
                        title="Profile"
                      />
                      <label
                        style={{
                          fontWeight: 500,
                          margin: 0,
                          padding: 0
                        }}>
                        {getInDecimal(self_deviation)}
                      </label>
                    </div>
                  </HoverableDIV>
                </div>
              </div>
              <ResponsiveContainer width={'100%'} height={250}>
                <ComposedChart
                  margin={{ top: 30, right: 10, bottom: 10, left: 30 }}
                  data={self_data_points}>
                  <YAxis
                    tickSize={10}
                    allowDecimals={true}
                    interval={0}
                    type="number"
                    domain={[0, 'auto']}>
                    <Label
                      offset={-4}
                      angle={-90}
                      value={pdYAxisValue ? pdYAxisValue : 'Amount(s)'}
                      position="insideLeft"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </YAxis>
                  <CartesianGrid stroke="#EAF0F9" strokeDasharray="5 5" />
                  <defs>
                    <linearGradient
                      id="histBarColor"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop offset="5%" stopColor="#003366" stopOpacity={1} />
                      <stop offset="95%" stopColor="#3366ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="currentBarColor"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop offset="5%" stopColor="green" stopOpacity={1} />
                      <stop offset="95%" stopColor="#3366ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <XAxis dataKey="date">
                    <Label
                      offset={-2}
                      value={pdXAxisValue ? pdXAxisValue : self_title}
                      position="insideBottom"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </XAxis>
                  <Bar dataKey="value" barSize={50}>
                    {self_data_points.map((ele, ind) => {
                      if (ele.date === 'T0 (Current)')
                        return <Cell fill="#F6D9AF" />;
                      else return <Cell fill="#F6D9AF" />;
                    })}
                  </Bar>
                  <Line
                    type="monotone"
                    strokeWidth="3"
                    dataKey="value"
                    stroke="#ff7300"
                    dot={true}
                  />
                  {self_data_points.map((ele, ind) => {
                    if (ele.date === 'T0 (Current)') {
                      return (
                        <ReferenceDot
                          x={ele.date}
                          y={ele.value}
                          r={5}
                          fill="red"
                          stroke="none">
                          <Label
                            value={`Val: ${formatDecimal(ele.value, 2)}`}
                            offset={5}
                            position="top"
                            style={{ fontSize: '10px', fill: 'black' }}
                          />
                        </ReferenceDot>
                      );
                    } else
                      return (
                        <ReferenceDot
                          x={ele.date}
                          y={ele.value}
                          r={5}
                          fill="green"
                          stroke="none">
                          <Label
                            value={`${formatDecimal(ele.value, 2)}`}
                            offset={9}
                            position="top"
                            style={{
                              fontSize: '12px',
                              fill: '#6A839A',
                              fontWeight: 400
                            }}
                          />
                        </ReferenceDot>
                      );
                  })}
                  <ReferenceLine y={self_mean} stroke="#6A839A" strokeWidth={2}>
                    <Label
                      offset={22}
                      value={'Mean ' + getInDecimal(self_mean)}
                      position="top"
                      fill="black"
                      fontSize="10"
                    />
                  </ReferenceLine>
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div
              className={classnames(
                'col-sm-6',
                style.padSide,
                style.graphBody
              )}>
              <div className={style.customerRiskHead}>
                <span>Peer Comparison</span>&nbsp;&nbsp;
                <div className="deviation" id="pdValue">
                  <HoverableDIV
                    firstDataPoint={
                      <GraphToolTip txt="Ranking of the customer's risk indicator value within its cluster." />
                    }>
                    <div className={style.graphIndicatorContainer}>
                      <img
                        style={{ marginRight: 5, height: 12 }}
                        src={
                          getInDecimal(peer_deviation) >= 0
                            ? deviation_up
                            : deviation_down
                        }
                        alt={
                          getInDecimal(peer_deviation) >= 0
                            ? deviation_up
                            : deviation_down
                        }
                        title="Deviation"
                      />
                      <label
                        style={{
                          fontWeight: 500,
                          margin: 0,
                          padding: 0
                        }}>
                        {getInDecimal(peer_deviation)}
                      </label>
                    </div>
                  </HoverableDIV>
                </div>
              </div>
              <ResponsiveContainer width={'100%'} height={250}>
                <ComposedChart
                  margin={{ top: 30, right: 10, bottom: 10, left: 40 }}
                  data={new_peer_data_points}>
                  <YAxis offset={-3} type="number" domain={[0, 'dataMax']}>
                    <Label
                      angle={-90}
                      value={pcYAxisValue ? pcYAxisValue : 'Frequency'}
                      position="insideLeft"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </YAxis>
                  <CartesianGrid stroke="lightgray" strokeDasharray="5 5" />
                  <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#003366" stopOpacity={1} />
                      <stop offset="95%" stopColor="#3366ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <XAxis dataKey="count">
                    <Label
                      offset={1}
                      value={pcXAxisValue ? pcXAxisValue : peer_title}
                      position="insideBottom"
                      style={{
                        textAnchor: 'middle',
                        fill: '#6A7088',
                        font: '500 10px Inter'
                      }}
                    />
                  </XAxis>
                  <Bar dataKey="value" barSize={100}>
                    {new_peer_data_points.map((ele, ind) => {
                      let color;
                      if (ind <= meanInd) {
                        //left half --unique color
                        color = colorArray[0];
                      } else {
                        color = colorArray[ind - meanInd]; //right half
                      }
                      if (ele.value === peer_current_value.value)
                        return <Cell fill="green" />; //current customer value
                      color = color ? color : [255, 255, 255]; //fallback color
                      return (
                        <Cell
                          fill={`rgb(${color[0]},${color[1]},${color[2]})`}
                        />
                      );
                    })}
                  </Bar>
                  <ReferenceLine
                    height={230}
                    isFront={true}
                    x={peerMeanLineIndex}
                    stroke="#6A7088"
                    strokeWidth={4}>
                    <Label
                      value="Mean"
                      position="insideTop"
                      offset={0}
                      content={(props) => {
                        return (
                          <text
                            style={{ fontSize: '10px' }}
                            x={props.viewBox.x + 15}
                            y={11}
                            textAnchor="end"
                            fill="black">
                            {props.value} {formatted_peer_mean}
                          </text>
                        );
                      }}
                    />
                    {/*<Label value="Mean" position="insideTop" fill='black' fontSize="14" />*/}
                  </ReferenceLine>
                  <ReferenceDot
                    alwaysShow={true}
                    isFront={true}
                    x={
                      customerDotIndex !== null
                        ? new_peer_data_points[customerDotIndex].count
                        : 0
                    }
                    y={
                      customerDotIndex !== null
                        ? Number(customerDotIndexYAxis).toFixed(2)
                        : null
                    }
                    r={5}
                    fill="#9D1600"
                    stroke="none">
                    <Label
                      dx={
                        customerDotIndex === peer_data_points.length - 1
                          ? -40
                          : 0
                      }
                      value={`Value: ${formatDecimal(
                        peer_current_value.riValue
                          ? peer_current_value.riValue
                          : 0,
                        2
                      )} (Percentile: ${peer_percentile})`}
                      offset={4}
                      position="top"
                      style={{ fontSize: '10px', fill: 'black' }}
                    />
                  </ReferenceDot>
                  <Tooltip content={<PeerComparisonToolTip />} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }

    // return null if none
    return (
      <div>
        <NoDataToDisplay />
      </div>
    );
  }
}

class CustomerRiskIndicator extends React.Component {
  // eslint-disable-line react/prefer-stateless-function
  constructor(props) {
    super(props);
    this.state = {
      riskIndicatorIndex: 1,
      isPopedOut: false
    };
    this.dataUrlMap = {};
  }

  loadFirst = () => {
    this.setState({
      riskIndicatorIndex: 1
    });
  };

  loadLast = (lastVal) => {
    this.setState({
      riskIndicatorIndex: lastVal
    });
  };

  loadNext = () => {
    const { riskIndicators } = this.props;
    if (this.state.riskIndicatorIndex !== riskIndicators.length) {
      this.setState({
        riskIndicatorIndex: this.state.riskIndicatorIndex + 1
      });
    }
  };

  loadPrevious = () => {
    if (this.state.riskIndicatorIndex !== 1) {
      this.setState({
        riskIndicatorIndex: this.state.riskIndicatorIndex - 1
      });
    }
  };

  loadSelected = (selectedName) => {
    const { riskIndicators } = this.props;
    const index = findIndex(riskIndicators, {
      riskIndicatorName: selectedName
    });
    this.setState({
      riskIndicatorIndex: index + 1
    });
  };

  togglePopout = () => {
    ['deviation'].forEach((title) => {
      let querySelector = `img[title='${title}']`;
      let image = document.querySelector(querySelector);
      if (image) {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        canvas.getContext('2d').drawImage(image, 0, 0);
        this.dataUrlMap[title] = canvas.toDataURL('image/png');
      }
    });

    this.setState({
      isPopedOut: !this.state.isPopedOut
    });
  };

  render() {
    const { clusterData, riskIndicators } = this.props;
    const { alertID } = getAlertIDFromURL();
    const { riskIndicatorIndex, isPopedOut } = this.state;
    const riskIndicator =
      Array.isArray(riskIndicators) && riskIndicators[riskIndicatorIndex - 1]
        ? riskIndicators[riskIndicatorIndex - 1]
        : null;

    const header_plural = <h4>Account Risk Indicators</h4>;

    let singularView = (
      <div className={classnames(style.card, 'mt-3')}>
        <div className={classnames(style.sectionHeader)}>
          <span className={style.assocTitle}>Risk Indicator</span>
        </div>
        <div></div>
        <div className={classnames(style.card_body)}>
          <RiskIndicatorBody
            loadPrevious={this.loadPrevious}
            clusterData={clusterData}
            togglePopout={this.togglePopout}
            riskIndicators={riskIndicators}
            isPluralView={false}
            loadSelected={this.loadSelected}
            loadNext={this.loadNext}
            load
            riskIndicator={riskIndicator}
          />
        </div>
      </div>
    );

    let pluralView = (
      <div style={{ margin: '20px' }}>
        <div className={style.customerRiskIndicatorWrapper}>
          {header_plural}
          <button className="btn btn-secondary" onClick={this.togglePopout}>
            Close
          </button>
          <div className="clearfix">
            {riskIndicators.map((ri, i) => (
              <div style={{ marginBottom: '10px' }}>
                <RiskIndicatorBody
                  isPluralView={true}
                  clusterData={clusterData}
                  riskIndicator={ri}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    if (!isPopedOut) {
      return singularView;
    } else
      return (
        <div>
          <PopoutWindow
            onPopupOutClose={this.togglePopout}
            title={alertID}
            width={window.innerWidth}
            height={900}
            imageUrlMap={this.dataUrlMap}>
            {pluralView}
          </PopoutWindow>
        </div>
      );
  }
}

CustomerRiskIndicator.propTypes = {};

export default CustomerRiskIndicator;
